import {
  customInstanceWithHeader,
  type ResponseWithHeaders,
} from '@fukuri-admins/apis/client/base/axiosInstance';
import {
  getGetApiV1ThreeLetterCodeClientAdminsClientNewslettersClientNewsletterIdUsersKey,
  getGetApiV1ThreeLetterCodeClientAdminsClientNewslettersClientNewsletterIdUsersUrl,
} from '@fukuri-admins/apis/client/gen/endpoints';
import type {
  GetApiV1ThreeLetterCodeClientAdminsClientNewslettersClientNewsletterIdUsers200,
  GetApiV1ThreeLetterCodeClientAdminsClientNewslettersClientNewsletterIdUsersParams,
} from '@fukuri-admins/apis/client/gen/model';
import { useFetch } from '@fukuri-admins/hooks/common';
import type { KeyedMutator } from 'swr';

type Params = {
  scope: GetApiV1ThreeLetterCodeClientAdminsClientNewslettersClientNewsletterIdUsersParams['scope'];
  read_status?: GetApiV1ThreeLetterCodeClientAdminsClientNewslettersClientNewsletterIdUsersParams['read_status'];
  page?: number;
};

const fetcher = (threeLetterCode: string, id: number, params: Params) =>
  customInstanceWithHeader<GetApiV1ThreeLetterCodeClientAdminsClientNewslettersClientNewsletterIdUsers200>(
    getGetApiV1ThreeLetterCodeClientAdminsClientNewslettersClientNewsletterIdUsersUrl(
      threeLetterCode,
      id,
      params
    ),
    { method: 'GET' }
  );

type Args = {
  threeLetterCode: string;
  id: number;
  scope: GetApiV1ThreeLetterCodeClientAdminsClientNewslettersClientNewsletterIdUsersParams['scope'];
  readStatus?: GetApiV1ThreeLetterCodeClientAdminsClientNewslettersClientNewsletterIdUsersParams['read_status'];
  page?: number;
  enabled?: boolean;
};

type IUseGetClientNewsletterUsers = (args: Args) => {
  data: GetApiV1ThreeLetterCodeClientAdminsClientNewslettersClientNewsletterIdUsers200;
  totalPage: number;
  totalCount: number;
  mutate: KeyedMutator<
    ResponseWithHeaders<GetApiV1ThreeLetterCodeClientAdminsClientNewslettersClientNewsletterIdUsers200>
  >;
};

/**
 * 社内報閲覧ユーザー一覧取得フック
 *
 * @param enabled - false の場合はフェッチをスキップする（未公開記事など）
 */
export const useGetClientNewsletterUsers: IUseGetClientNewsletterUsers = ({
  threeLetterCode,
  id,
  scope,
  readStatus,
  page,
  enabled = true,
}) => {
  const params: Params = { scope, read_status: readStatus, page };

  const { data: response, mutate } = useFetch<
    ResponseWithHeaders<GetApiV1ThreeLetterCodeClientAdminsClientNewslettersClientNewsletterIdUsers200>
  >(
    enabled
      ? getGetApiV1ThreeLetterCodeClientAdminsClientNewslettersClientNewsletterIdUsersKey(
          threeLetterCode,
          id,
          params
        )
      : undefined,
    () => fetcher(threeLetterCode, id, params),
    { suspense: true }
  );

  if (!response) {
    return { data: { client_newsletter_users: [] }, totalPage: 0, totalCount: 0, mutate };
  }

  const data = response.data;
  const totalPage = Number(response.headers['total-pages']) || 0;
  const totalCount = Number(response.headers['total-count']) || 0;

  return { data, totalPage, totalCount, mutate };
};
